import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import createLogger from 'redux-logger';
import { reducer as formReducer } from 'redux-form';
import createSagaMiddleware from 'redux-saga';
import reduxReset from 'redux-reset';
import createHistory from 'history/createBrowserHistory';
import { routerMiddleware, routerReducer } from 'react-router-redux';
import PromiseCreator from 'promise-creator';
import get from 'lodash.get';
import alert from './alert';
import auth, { AUTH_LEVEL_CHECK, LOGOUT, rootSaga as authSaga, TOKEN_UPDATED } from './auth';
import users, { rootSaga as usersSaga } from './users';
import me, { rootSaga as meSaga } from './me';
import assessments, { rootSaga as assessmentsSaga } from './assessments';
import career, { rootSaga as careerSaga } from './career';
import theme, { rootSaga as themeSaga } from './theme';
import report, { rootSaga as reportSaga } from './report';
import programs, { rootSaga as programsSaga } from './programs';
import prompt, { openPromptAction, rootSaga as promptSaga } from './prompt';
import jobs, { rootSaga as jobsSaga } from './jobs';
import sites, { rootSaga as sitesSaga } from './sites';
import industries, { rootSaga as industriesSaga } from './industries';
import positions, { rootSaga as positionsSaga } from './company-positions';
import snackbar, { rootSaga as snackbarSaga } from './snackbar';
import supportWidget, { rootSaga as supportWidgetSaga } from './support-widget';
import notifications, { rootSaga as notificationsSaga } from './notifications';
import app, { RESET_STATE, rootSaga as appSaga } from './app';
import filter from './filter';
import filterPrograms from './filterPrograms';
import admin, { rootSaga as adminSaga } from './admin';
import employerSettings, { rootSaga as employerSettingsSaga } from './employer-settings';
import usersSearch, { rootSaga as usersSearchSaga } from './users-search';
import programsAndSchools, { rootSaga as programsAndSchoolsSaga } from './programs-schools';
import candidate, { rootSaga as candidateSaga } from './candidate';
import colleges, { rootSaga as collegesSaga } from './colleges';
import lock, { rootSaga as lockSaga } from './lock';
import favoriteCareers, { rootSaga as favoriteCarrersSaga } from './favorite-careers';
import favoritePrograms, { rootSaga as favoriteProgramsSaga } from './favorite-programs';
import favoriteProgramsCareer, {
  rootSaga as favoriteProgramsCareerSaga,
} from './favorite-programs-career';
import managePosition, { rootSaga as managePositionSaga } from './manage-position';
import locationHistory, { rootSaga as locationHistorySaga } from './location-history';
import collegePrograms, { rootSaga as collegeProgramsSaga } from './college-programs';
import managePrograms, { rootSaga as manageProgramsSaga } from './manage-programs';
import militaryCrosswalk, { rootSaga as militaryCrosswalkSaga } from './military-crosswalk';
import demographic, { rootSaga as demographicSaga } from './demographic';
import petersons, { rootSaga as petersonsSaga } from './petersons';
import journeyCards, { rootSaga as journeyCardsSaga } from './journey-cards';
import intake, { rootSaga as intakeSaga } from './intake';
import windowState from './window-resize';
import pgHomeLink from './pg-home-link';
import persist from '../lib/persist-saga';
import actionKey from '../utils/action-key';

const { saga: persistSaga, getInitialState } = persist({
  clear: [LOGOUT],
  always: [TOKEN_UPDATED, actionKey.success(AUTH_LEVEL_CHECK)],
  selector: state => ({
    auth: {
      token: state.auth.token,
      level: state.auth.level,
      inviteCode: state.auth.inviteCode,
      universalLogin: state.auth.universalLogin,
    },
    me: state.me,
    theme: state.theme,
    users: {
      list: state.users.list,
    },
    colleges: {
      list: state.colleges.list,
      connections: state.colleges.connections,
      sponsored: state.colleges.sponsored,
      disableConnect: state.colleges.disableConnect,
    },
    app: state.app,
    lock: state.lock,
    notifications: state.notifications,
    locationHistory: state.locationHistory,
    petersons: state.petersons,
    intake: state.intake,
    journeyCards: state.journeyCards,
    pgHomeLink: state.pgHomeLink,
  }),
  throttle: 1000,
  shouldPersist: state => get(state, 'theme.companyDomain') !== 'login',
});


const initialize = (initialState, { history, options = [] } = {}) => {
  const routerHistory = history || createHistory({
    getUserConfirmation: (action, callback) => {
      if (!action) {
        callback(true);
        return;
      }
      const { promise, resolve } = PromiseCreator();
      const promptOptions = JSON.parse(action);
      store.dispatch(openPromptAction({ resolve, ...promptOptions }));
      promise.then((confirm) => {
        callback(confirm);
      });
    },
  });

  const sagaMiddleware = createSagaMiddleware();
  const middleware = [sagaMiddleware, routerMiddleware(routerHistory)];

  if (process.env.NODE_ENV === 'development') {
    middleware.push(createLogger());
  }

  const createStoreWithMiddleware = compose(
    applyMiddleware(
      ...middleware,
    ),
    reduxReset(RESET_STATE),
  )(createStore);

  const state = initialState || getInitialState();
  const store = createStoreWithMiddleware(
    combineReducers({
      alert,
      auth,
      users,
      theme,
      report,
      filter,
      filterPrograms,
      career,
      me,
      app,
      assessments,
      prompt,
      jobs,
      programs,
      snackbar,
      sites,
      admin,
      industries,
      positions,
      programsAndSchools,
      usersSearch,
      candidate,
      colleges,
      lock,
      notifications,
      managePosition,
      employerSettings,
      favoriteCareers,
      favoriteProgramsCareer,
      favoritePrograms,
      managePrograms,
      form: formReducer,
      router: routerReducer,
      locationHistory,
      collegePrograms,
      militaryCrosswalk,
      demographic,
      intake,
      petersons,
      journeyCards,
      windowState,
      pgHomeLink,
      supportWidget,
    }),
    state,
    ...options,
  );
  const sagaMiddlewareTask = sagaMiddleware.run(function* rootSaga() {
    return yield [
      persistSaga(),
      authSaga(),
      usersSaga(),
      reportSaga(),
      careerSaga(),
      meSaga(),
      themeSaga(),
      assessmentsSaga(),
      promptSaga(),
      snackbarSaga(),
      appSaga(),
      jobsSaga(),
      sitesSaga(),
      adminSaga(),
      industriesSaga(),
      programsSaga(),
      positionsSaga(),
      notificationsSaga(),
      employerSettingsSaga(),
      programsAndSchoolsSaga(),
      usersSearchSaga(),
      candidateSaga(),
      collegesSaga(),
      lockSaga(),
      favoriteCarrersSaga(),
      favoriteProgramsCareerSaga(),
      favoriteProgramsSaga(),
      managePositionSaga(),
      locationHistorySaga(),
      collegeProgramsSaga(),
      manageProgramsSaga(),
      militaryCrosswalkSaga(),
      demographicSaga(),
      intakeSaga(),
      petersonsSaga(),
      journeyCardsSaga(),
      supportWidgetSaga(),
    ];
  });
  return {
    store,
    sagaMiddlewareTask,
    history: routerHistory,
  };
};

export default initialize;



// WEBPACK FOOTER //
// ./src/client/redux/index.js